import React from "react";

import {
  FaBolt,
  FaCheckCircle,
  FaExternalLinkAlt,
} from "react-icons/fa";

import LandingButton from "../../../components/Button/LandingButton";

import {
  TOURNAMENT_OVERVIEW_CHECKS,
  TOURNAMENT_OVERVIEW_FEATURES,
} from "../data/tournamentAboutData";

import useInViewOnce from "../hooks/useInViewOnce";

function OverviewFeatureCard({
  icon: Icon,
  title,
  description,
}) {
  return (
    <article className="group rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-orange-200 hover:shadow-xl hover:shadow-orange-500/10 dark:border-white/10 dark:bg-white/[0.04] dark:hover:border-orange-400/30">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-orange-600 ring-1 ring-orange-100 transition group-hover:bg-orange-500 group-hover:text-white dark:bg-orange-500/15 dark:text-orange-300 dark:ring-orange-400/20">
        <Icon className="h-5 w-5" />
      </div>

      <h3 className="mt-4 text-base font-bold text-slate-950 dark:text-white">
        {title}
      </h3>

      <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">
        {description}
      </p>
    </article>
  );
}

export default function TournamentAboutOverview({
  onDemoClick,
}) {
  const {
    ref,
    isVisible,
  } = useInViewOnce();

  return (
    <div
      ref={ref}
      className={`grid items-center gap-8 transition-all duration-700 lg:grid-cols-[0.9fr_1.1fr] lg:gap-12 ${
        isVisible
          ? "translate-y-0 opacity-100"
          : "translate-y-8 opacity-0"
      }`}
    >
      {/* Illustration */}
      <div className="relative">
        <div className="pointer-events-none absolute -left-10 top-8 h-60 w-60 rounded-full bg-orange-300/20 blur-3xl dark:bg-orange-500/10" />

        <div className="pointer-events-none absolute -bottom-8 -right-8 h-60 w-60 rounded-full bg-blue-300/20 blur-3xl dark:bg-blue-500/10" />

        <div className="relative overflow-hidden rounded-[34px] border border-slate-200 bg-white p-4 shadow-2xl shadow-slate-950/10 dark:border-white/10 dark:bg-slate-900 sm:p-6">
          <div className="overflow-hidden rounded-[26px] bg-slate-950">
            <img
              src="/images/Tournoi03.jpg"
              alt="MonAppliTournoi utilisé pendant une compétition de judo"
              className="aspect-[4/3] w-full object-cover"
              loading="lazy"
            />
          </div>

          <div className="mt-5 rounded-[26px] bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 p-5 text-white">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-orange-300">
              <FaBolt />
              Pensé pour le jour J
            </div>

            <p className="mt-3 text-2xl font-bold leading-tight sm:text-3xl">
              Une compétition plus fluide pour les organisateurs, les tables et les coachs.
            </p>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-orange-50 p-4 dark:bg-orange-500/10">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-orange-600 dark:text-orange-300">
                Préparation
              </p>

              <p className="mt-1 font-bold text-slate-950 dark:text-white">
                Centralisée
              </p>
            </div>

            <div className="rounded-2xl bg-blue-50 p-4 dark:bg-blue-500/10">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-blue-600 dark:text-blue-300">
                Compétition
              </p>

              <p className="mt-1 font-bold text-slate-950 dark:text-white">
                Suivie en direct
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-400/20 dark:bg-orange-500/10 dark:text-orange-300">
          <FaBolt />
          Fonctionnalités principales
        </div>

        <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl lg:text-5xl dark:text-white">
          Toute l’organisation du tournoi dans une seule application
        </h2>

        <p className="mt-5 text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
          MonAppliTournoi centralise la préparation, les inscriptions, les pesées, la répartition des combattants, les tables d’arbitrage et les résultats publics. Chaque intervenant dispose des informations nécessaires sans multiplier les fichiers et les manipulations.
        </p>

        <div className="mt-7 grid gap-4 sm:grid-cols-2">
          {TOURNAMENT_OVERVIEW_FEATURES.map(
            (feature) => (
              <OverviewFeatureCard
                key={feature.title}
                {...feature}
              />
            ),
          )}
        </div>

        <div className="mt-7 grid gap-3 sm:grid-cols-2">
          {TOURNAMENT_OVERVIEW_CHECKS.map(
            (item) => (
              <div
                key={item}
                className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.03]"
              >
                <FaCheckCircle className="mt-1 shrink-0 text-emerald-500" />

                <span className="text-sm font-medium leading-6 text-slate-700 dark:text-slate-200">
                  {item}
                </span>
              </div>
            ),
          )}
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <LandingButton
            variant="primary"
            onClick={onDemoClick}
          >
            Voir le site en ligne

            <FaExternalLinkAlt className="ml-2 text-xs" />
          </LandingButton>

          <a
            href="#tournoi-contact"
            className="inline-flex min-h-12 items-center justify-center rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-600 dark:border-white/10 dark:bg-slate-900 dark:text-white dark:hover:border-orange-400/30 dark:hover:bg-orange-500/10 dark:hover:text-orange-300"
          >
            Demander une présentation
          </a>
        </div>
      </div>
    </div>
  );
}